import React, {useState} from 'react';

import {NoResultsRow} from 'src/components/no-results-row/no-results-row';
import {Separator} from 'src/components/separator';
import {UserName} from 'src/components/user-name/user-name';
import {COLUMN_CAPTIONS, ColumnTypes, ColumnWidth} from 'src/modules/admin/ai-assist-usage/constants';
import {AiAssistUsageDetail} from 'src/modules/admin/ai-assist-usage/views/ai-assist-usage-detail';
import {SlideButton} from 'src/modules/admin/ai-assist-usage/views/slide-button';
import {AiReviewUsageBasicInfo} from 'src/openapi/models/AiReviewUsageBasicInfo';

import EllipsisTextHint from '@pb/kit-component-ellipsis-text-hint';
import {Group} from '@pb/kit-component-group';
import {Loading} from '@pb/kit-component-loading';
import {Rating} from '@pb/kit-component-rating';
import Table, {Body, Cell, Head, Row} from '@pb/kit-component-table';
import {Typography} from '@pb/kit-component-typography';
import {formatDate} from '@pb/kit-formatters';

type Props = {
    aiReviewUsageBasicInfo: AiReviewUsageBasicInfo[],
    isLoading: boolean,
};

export const AiAssistUsageView = (props: Props) => {
    const {
        isLoading,
        aiReviewUsageBasicInfo,
    } = props;

    const [expandedRow, setExpandedRow] = useState<number | null>(null);

    const onToggleSlideBox = (index: number) => {
        setExpandedRow(prev => (prev === index ? null : index));
    };

    if (isLoading) {
        return (
            <Loading fast={true}/>
        );
    }

    return (
        <Group direction="v" className="pt-xxl">
            <Typography variant="heading-level-5">
                <strong>
                    AI Assist Usage
                </strong>
            </Typography>
            <Separator/>
            <Table>
                <Head>
                    <Cell width="3rem"/>
                    <Cell width={ColumnWidth[ColumnTypes.EVENT_TYPE]}>{COLUMN_CAPTIONS[ColumnTypes.EVENT_TYPE]}</Cell>
                    <Cell width={ColumnWidth[ColumnTypes.EVENT_SOURCE]}>
                        {COLUMN_CAPTIONS[ColumnTypes.EVENT_SOURCE]}
                    </Cell>
                    <Cell width={ColumnWidth[ColumnTypes.EVENT_CODE]}>{COLUMN_CAPTIONS[ColumnTypes.EVENT_CODE]}</Cell>
                    <Cell width={ColumnWidth[ColumnTypes.AUTHOR]}>{COLUMN_CAPTIONS[ColumnTypes.AUTHOR]}</Cell>
                    <Cell width={ColumnWidth[ColumnTypes.RUNS]}>{COLUMN_CAPTIONS[ColumnTypes.RUNS]}</Cell>
                    <Cell width={ColumnWidth[ColumnTypes.SATISFACTION]}>
                        {COLUMN_CAPTIONS[ColumnTypes.SATISFACTION]}
                    </Cell>
                    <Cell width={ColumnWidth[ColumnTypes.DATE]}>{COLUMN_CAPTIONS[ColumnTypes.DATE]}</Cell>
                </Head>
                <Body>
                    {aiReviewUsageBasicInfo.length ? (
                        aiReviewUsageBasicInfo.map((row, index) => (
                            <>
                                <Row key={index} showCustomZebra={false}>
                                    <Cell verticalAlign="middle">
                                        <SlideButton
                                            index={index}
                                            expanded={expandedRow === index}
                                            onToggle={onToggleSlideBox}
                                        />
                                    </Cell>
                                    <Cell><EllipsisTextHint>{row.eventType}</EllipsisTextHint></Cell>
                                    <Cell><EllipsisTextHint>{row.eventSource}</EllipsisTextHint></Cell>
                                    <Cell><EllipsisTextHint>{row.eventCode}</EllipsisTextHint></Cell>
                                    <Cell><UserName email={row.authorEmail}/></Cell>
                                    <Cell>{row.runsCount}</Cell>
                                    <Cell><Rating grade={row.averageRating} readonly={true}/></Cell>
                                    <Cell>{formatDate(row.changeRequestUpdateDate)}</Cell>
                                </Row>
                                <Cell colSpan={7}>
                                    <AiAssistUsageDetail
                                        expanded={expandedRow === index}
                                        changeRequestId={row.changeRequestId}
                                    />
                                </Cell>
                            </>
                        ))
                    ) : <NoResultsRow colSpan={7}/>}
                </Body>
            </Table>
        </Group>
    );
};
